import { RefObject, useEffect, useRef } from "react";

import { TConfiguredTracker } from "./createTrackerConfig";

/**
 * The element must reach a quarter up from the viewport's bottom edge. A ratio
 * threshold would never fire for elements taller than the viewport.
 */
export const VIEW_ROOT_MARGIN = "0px 0px -25% 0px";

interface ViewTrackingOptions<TEvent extends string> {
  /** The host page's service tracker, which stamps its own page props */
  track: TConfiguredTracker<TEvent>;
  event: TEvent;
  /** Read when the view fires, so they can change without re-observing */
  properties?: Record<string, unknown>;
  rootMargin?: string;
  /** How long the element must stay in view to count, so scrolling past it does not */
  minVisibleMs?: number;
}

/**
 * Tracks `event` the first time `ref`'s element stays in view for
 * `minVisibleMs`, once per mount, then stops observing.
 *
 * @example
 * const viewRef = useRef<HTMLElement>(null);
 * useViewTracking(viewRef, {
 *   track: DashboardService.track,
 *   event: DASHBOARD_EVENTS.YEARLY_LEDGER_VIEWED,
 * });
 * return <section ref={viewRef}>…</section>;
 */
export function useViewTracking<TEvent extends string>(
  ref: RefObject<Element>,
  {
    track,
    event,
    properties,
    rootMargin = VIEW_ROOT_MARGIN,
    minVisibleMs = 0,
  }: ViewTrackingOptions<TEvent>,
) {
  // Survives the effect re-running, so an element already reported stays
  // reported even if the observer is torn down and rebuilt.
  const hasReported = useRef(false);

  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    const element = ref.current;
    if (!element || hasReported.current) return;

    let visibleTimer: ReturnType<typeof setTimeout> | undefined;

    const report = () => {
      hasReported.current = true;
      observer.disconnect();

      track(event, propertiesRef.current);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (hasReported.current) return;

        // Leaving before the dwell time is up cancels the pending view
        if (!entry.isIntersecting) {
          clearTimeout(visibleTimer);
          return;
        }

        if (minVisibleMs === 0) {
          report();
          return;
        }

        clearTimeout(visibleTimer);
        visibleTimer = setTimeout(report, minVisibleMs);
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => {
      clearTimeout(visibleTimer);
      observer.disconnect();
    };
  }, [ref, track, event, rootMargin, minVisibleMs]);
}
